import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import { setDialog, setLoading } from "slices/appSlice";
import { useDispatch } from "react-redux";
import Api from "api";

const OffCycleVarianceDialog = ({
  hpKey,
  open,
  onClose,
  policyKey,
  policyNumber,
  policyTitle,
  actionType,
  title,
  columnsName,
  reloadGrid,
}) => {
  const [changeControl, setChangeControl] = useState("");
  const [lobData, setLobData] = useState(null);
  const dispatch = useDispatch();

  // Load Change Control text for this policy/plan
  useEffect(() => {
    let active = true;
    const loadChangeControl = async () => {
      if (!open || !hpKey || !policyKey) return;
      try {
        const { data } = await Api.policyData.getPolicyChangeControlData(
          hpKey,
          policyKey
        );
        if (!active) return;
        // Set from changeControlText; default to empty string if missing
        setChangeControl(data?.changeControlText ?? "");
      } catch (error) {
        // Non-blocking: show error but keep dialog usable
        dispatch(
          setDialog({
            message: error?.response?.data?.errors || error.message,
            title: error?.code ? `${error.code} :: ${error.message}` : "Error",
          })
        );
      }
    };
    loadChangeControl();
    return () => {
      active = false;
    };
  }, [open, hpKey, policyKey, dispatch]);

  const getLineOfBusinessDetails = async (hpKey, policyKey) => {
    try {
      const response = await Api.policyEnforcementGrid.getPolicyLobConfig(
        hpKey,
        policyKey
      );
      return response.data; // Assuming the API returns the LOB details list
    } catch (error) {
      setDialog({
        message: error.response?.data?.errors,
        title: error.code + " :: " + error.message,
      });
      return [];
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const data = await getLineOfBusinessDetails(hpKey, policyKey);
      const formattedData = data.map((lob) => ({
        id: lob.id,
        lobDesc: lob.lobDesc,
        decision: lob.adoptionDecision,
        effectiveDate: lob.effectiveDate,
        comments: lob.varianceComments,
      }));
      setLobData(formattedData);
    };
    fetchData();
  }, [hpKey, policyKey]);

  const handleDecisionChange = (index, value) => {
    const updatedLobData = [...lobData];
    updatedLobData[index].decision =
      value === "AWV" ? "AWV" : value === "AT" ? "AT" : "";
    if (value === "") {
      updatedLobData[index].effectiveDate = null;
      updatedLobData[index].comments = "";
    } else if (value == "AT") {
      updatedLobData[index].effectiveDate = null;
      updatedLobData[index].comments = "";
    }
    // const updatedLobData = [...lobData];
    // updatedLobData[index].decision =
    //   value === "AWV" ? "AWV" : "AT";
    // if (value === "AT") {
    //   updatedLobData[index].effectiveDate = null;
    //   updatedLobData[index].comments = "";
    // }
    setLobData(updatedLobData);
  };

  const handleEffectiveDateChange = (index, value) => {
    const updatedLobData = [...lobData];
    updatedLobData[index].effectiveDate = value;
    setLobData(updatedLobData);
  };

  const handleCommentsChange = (index, value) => {
    const updatedLobData = [...lobData];
    updatedLobData[index].comments = value;
    setLobData(updatedLobData);
  };

  const handleSave = () => {
    if (!changeControl.trim()) {
      dispatch(setDialog({ message: "Change Control is required." }));
      return;
    }

    for (const lob of lobData) {
      if (!lob.decision) {
        dispatch(
          setDialog({
            message: `Variance Decision is required for ${lob.lobDesc}.`,
          })
        );
        return;
      }
      if (lob.decision === "AWV" && !lob.effectiveDate) {
        dispatch(
          setDialog({
            message: `Effective Date is required for ${lob.lobDesc} when decision is Yes.`,
          })
        );
        return;
      }
      if (lob.decision === "AWV" && (!lob.comments || !lob.comments?.trim())) {
        dispatch(
          setDialog({
            message: `Variance Comments is required for ${lob.lobDesc} when decision is Yes.`,
          })
        );
        return;
      }
    }

    const dto = buildPolicyEnforcementRuleDto();

    dispatch(setLoading(true));
    Api.policyEnforcementGrid
      .saveAdoptionDecisions([dto], true)
      .then(() => {
        dispatch(setDialog({ message: `${actionType} saved successfully.` }));
        reloadGrid();
        onClose();
      })
      .catch((error) => {
        dispatch(
          setDialog({
            message: error.response?.data?.errors || error.message,
            title: error.code ? error.code + " :: " + error.message : "Error",
          })
        );
      })
      .finally(() => {
        dispatch(setLoading(false));
      });
  };

  const buildPolicyEnforcementRuleDto = () => {
    if (!lobData || lobData.length === 0) {
      throw new Error("No LOB data to save");
    }

    const today = new Date();
    const year = today.getFullYear();
    const quarter = `Q${Math.floor(today.getMonth() / 3) + 1}`;

    return {
      policyKey,
      policyNumber,
      policyTitle,
      quarter,
      year,
      healthPlanKey: hpKey,
      changeControlText: changeControl,
      actionType,
      coding: { status: "Assigned" },
      translation: { status: "Assigned" },
      configuration: { status: "Pending Translation" },
      lineOfBusinessDetailsList: lobData.map((lob) => ({
        id: lob.id,
        lobDesc: lob.lobDesc,
        adoptionDecision: lob.decision,
        stickyDecision: lob.stickyDecision ?? false,
        effectiveDate: lob.effectiveDate,
        stickyDate: lob.stickyDate ?? false,
        endDate: lob.endDate ?? null,
        varianceComments: lob.comments,
      })),
    };
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between" }}>
        <Typography variant="h5" component="span" sx={{ fontWeight: "bold" }}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", gap: 2 }}>
          <Typography sx={{ fontWeight: "bold", marginTop: 1 }}>
            Change Control
          </Typography>
          <TextField
            label="Change Control (required)"
            value={changeControl}
            onChange={(e) => setChangeControl(e.target.value)}
          />
        </Box>
      </DialogTitle>
      <Typography sx={{ paddingLeft: "24px" }}>
        Policy: {policyNumber} - {policyTitle}
      </Typography>
      <DialogContent>
        {lobData === null ? (
          <Typography variant="body1" sx={{ padding: "16px" }}>
            Loading Line of Business data...
          </Typography>
        ) : lobData.length === 0 ? (
          <Typography variant="body1" sx={{ padding: "16px" }}>
            No Line of Business data available.
          </Typography>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    padding: "16px 0",
                    width: "26%",
                    fontSize: "16px",
                  }}
                >
                  {columnsName.LINE_OF_BUSINESS}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    padding: "16px 0",
                    width: "22%",
                    fontSize: "16px",
                  }}
                >
                  {columnsName.OPTIONS}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    padding: "16px 0",
                    width: "22%",
                    fontSize: "16px",
                  }}
                >
                  {columnsName.EFFECTIVE_DATE}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: "bold",
                    padding: "16px 0",
                    fontSize: "16px",
                    width: "28%",
                  }}
                >
                  {columnsName.COMMENTS}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lobData.map((lob, index) => (
                <TableRow key={lob.lobDesc}>
                  <TableCell sx={{ padding: "16px 8px 16px 0" }}>
                    {lob.lobDesc}
                  </TableCell>
                  <TableCell sx={{ padding: "16px 8px 16px 0" }}>
                    <TextField
                      select
                      value={lob.decision}
                      onChange={(e) =>
                        handleDecisionChange(index, e.target.value)
                      }
                      fullWidth
                    >
                      <MenuItem value="AWV">Yes</MenuItem>
                      <MenuItem value="AT">No</MenuItem>
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ padding: "16px 8px 16px 0" }}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DesktopDatePicker
                        value={
                          lob.decision !== "AWV"
                            ? null
                            : dayjs(lob.effectiveDate)
                        }
                        onChange={(value) =>
                          handleEffectiveDateChange(index, value)
                        }
                        disabled={lob.decision !== "AWV"}
                        slotProps={{
                          textField: { fullWidth: true },
                        }}
                      />
                    </LocalizationProvider>
                  </TableCell>
                  <TableCell sx={{ padding: "16px 0" }}>
                    <TextField
                      value={lob.decision === "AWV" ? lob.comments || "" : ""}
                      onChange={(e) =>
                        handleCommentsChange(index, e.target.value)
                      }
                      disabled={lob.decision !== "AWV"}
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          <strong>Cancel</strong>
        </Button>
        <Button onClick={handleSave} color="primary">
          <strong>Save</strong>
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OffCycleVarianceDialog;
